// Site settings — the configuration hub.
//
// Every configure-once surface used to be its own top-level nav row, because
// there was nowhere else for it to live: /setup evicts you to /sign-in the
// moment setup completes (SetupPage.tsx), so it cannot host reconfiguration.
// That left a master_admin looking at 24-29 nav items, most of which they
// touch once at install. See docs/ux-review/NAVIGATION_AUDIT.md.
//
// /admin/settings is not a new idea — the dashboard's failed-sync alert was
// already linking here before anyone built it (UX-004 in the first audit).
//
// This is a hub, not a wrapper: each card links to the page that already
// exists, unchanged, so deep links and bookmarks keep working. What the hub
// adds is the answer to "is this configured?" without having to open six
// pages to find out.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon, Spinner, StatusChip, type StatusState } from '@ng-unifi/ui';
import { apiClient, ApiError } from '../lib/api.js';

interface SetupStatus {
  completed: boolean;
  site_name: string;
  site_mode: string;
  has_master: boolean;
  has_unifi: boolean;
  mfa_enforced: boolean;
}

interface WebhookHealth {
  registration_status: 'registered' | 'failed' | 'not_attempted' | 'unknown';
  controller_verified: boolean | null;
  last_event_at: string | null;
}

interface CredentialPolicy {
  touch_credential_enabled: boolean;
  third_party_nfc_enabled: boolean;
  pin_code_enabled: boolean;
}

/** A probe that could not run is `unknown`, never a healthy default. */
type Probe = { state: StatusState; label: string; detail?: string };

function unifiProbe(setup: SetupStatus | null, err: ApiError | null): Probe {
  if (err) return { state: 'unavailable', label: 'Unknown', detail: 'Setup status did not answer.' };
  if (!setup) return { state: 'unknown', label: 'Checking' };
  return setup.has_unifi
    ? { state: 'applied', label: 'Connected' }
    : { state: 'failed', label: 'Not connected', detail: 'The portal has no UniFi controller credentials.' };
}

function webhookProbe(health: WebhookHealth | null, err: ApiError | null): Probe {
  if (err) return { state: 'unavailable', label: 'Unknown', detail: 'Webhook health did not answer.' };
  if (!health) return { state: 'unknown', label: 'Checking' };
  if (health.registration_status === 'failed') {
    return { state: 'failed', label: 'Registration failed' };
  }
  if (health.registration_status === 'not_attempted') {
    return { state: 'pending', label: 'Not registered' };
  }
  if (health.registration_status === 'unknown') {
    return { state: 'unknown', label: 'Unknown' };
  }
  // Registered. Controller verification is a separate fact and stays separate.
  if (health.controller_verified === true) return { state: 'applied', label: 'Verified' };
  if (health.controller_verified === false) {
    return { state: 'pending', label: 'Registered, unverified' };
  }
  return { state: 'unknown', label: 'Registered, verification unknown' };
}

/**
 * Which credential types are enabled is a *setting*, not a health state, and
 * it does not belong in a StatusChip. Rendering "fobs, phone passes, PINs" in
 * the applied/green tone reads as "this is good" — but the deployed-smoke
 * runbook treats a fail-closed policy (fobs and phone passes off) as the safe
 * production default, so green would be signalling the opposite of the truth.
 *
 * Using a status vocabulary to show a configuration value is the same
 * conflation the rest of this audit argued against. It is plain text.
 */
function policyValue(policy: CredentialPolicy | null): string | undefined {
  if (!policy) return undefined;
  const on = [
    policy.third_party_nfc_enabled ? 'fobs' : null,
    policy.touch_credential_enabled ? 'phone passes' : null,
    policy.pin_code_enabled ? 'PINs' : null,
  ].filter(Boolean);
  return on.length === 0 ? 'No credential types enabled' : `Enabled: ${on.join(', ')}`;
}

interface Section {
  href: string;
  icon: string;
  title: string;
  body: string;
  /** A health state — reachable, verified, failing. Only where one exists. */
  probe?: Probe;
  /** A current setting, shown as text. Never dressed up as a health state. */
  value?: string;
}

export function AdminSettingsPage() {
  const [setup, setSetup] = useState<SetupStatus | null>(null);
  const [setupErr, setSetupErr] = useState<ApiError | null>(null);
  const [health, setHealth] = useState<WebhookHealth | null>(null);
  const [healthErr, setHealthErr] = useState<ApiError | null>(null);
  const [policy, setPolicy] = useState<CredentialPolicy | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const opts = { skipTenantScope: true } as const;
    Promise.allSettled([
      apiClient.get<{ data: SetupStatus }>('/api/v1/setup/status', opts),
      apiClient.get<{ data: WebhookHealth }>('/api/v1/admin/webhooks/health', opts),
      apiClient.get<{ data: CredentialPolicy }>('/api/v1/admin/credentials/policy', opts),
    ]).then(([s, w, p]) => {
      if (cancelled) return;
      // Each probe reports its own failure. One unreachable endpoint must not
      // blank the page or, worse, read as "fine".
      if (s.status === 'fulfilled') setSetup(s.value.data);
      else setSetupErr(s.reason instanceof ApiError ? s.reason : new ApiError('Network failure', 0, 'network'));
      if (w.status === 'fulfilled') setHealth(w.value.data);
      else setHealthErr(w.reason instanceof ApiError ? w.reason : new ApiError('Network failure', 0, 'network'));
      if (p.status === 'fulfilled') setPolicy(p.value.data);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const sections: Section[] = [
    {
      href: '/admin/unifi-setup-wizard',
      icon: 'lan',
      title: 'UniFi controller',
      body: 'Connect the portal to a controller, or re-run the connection wizard.',
      probe: unifiProbe(setup, setupErr),
    },
    {
      href: '/admin/webhooks',
      icon: 'webhook',
      title: 'Webhooks',
      body: 'Where the controller sends events, and whether they are arriving.',
      probe: webhookProbe(health, healthErr),
    },
    {
      href: '/admin/credentials',
      icon: 'key',
      title: 'Credential policy',
      body: 'Which credential types operators may issue.',
      value: policyValue(policy),
    },
    {
      href: '/admin/branding',
      icon: 'palette',
      title: 'Site name and branding',
      body: 'The name, colours and terminology this install shows its users.',
    },
    {
      href: '/admin/settings/default-tenant',
      icon: 'apartment',
      title: 'Default tenant',
      body: 'Which tenant single-tenant and multi-family installs operate as.',
    },
    {
      href: '/admin/credentials/inventory',
      icon: 'inventory_2',
      title: 'Credential inventory',
      body: 'Every credential issued across the site, and who holds it.',
    },
    {
      href: '/admin/pre-launch',
      icon: 'checklist',
      title: 'Pre-launch checklist',
      body: 'Readiness checks to run before real users are let in.',
    },
  ];

  return (
    <div className="space-y-6">
      <header className="flex items-center gap-4">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary-container text-on-primary">
          <Icon name="settings" className="text-2xl" />
        </div>
        <div>
          <h1 className="font-headline-md text-headline-md text-on-surface">Site settings</h1>
          <p className="font-body-md text-body-md text-on-surface-variant">
            {setup?.site_name
              ? `Configuration for ${setup.site_name}. Set once, changed rarely.`
              : 'Configuration for this install. Set once, changed rarely.'}
          </p>
        </div>
      </header>

      {loading ? (
        <div className="flex justify-center p-12">
          <Spinner />
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {sections.map((s) => (
            <Link
              key={s.href}
              to={s.href}
              className="flex flex-col gap-3 rounded-xl border border-outline-variant bg-surface-container-lowest p-5 transition-colors hover:bg-surface-container-low focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {/* Fixed-height row: without it, the cards that have no health
                  state sat shorter than the ones that do, and the gap read as
                  something having failed to load rather than as a section
                  that simply has nothing to report. */}
              <div className="flex min-h-[2.5rem] items-start justify-between gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-surface-container text-on-surface">
                  <Icon name={s.icon} />
                </span>
                {s.probe ? (
                  <StatusChip
                    state={s.probe.state}
                    label={s.probe.label}
                    detail={s.probe.detail}
                  />
                ) : null}
              </div>
              <div>
                <span className="block font-title-sm text-title-sm text-on-surface">{s.title}</span>
                <span className="mt-1 block font-body-md text-body-md text-on-surface-variant">
                  {s.body}
                </span>
                {s.value ? (
                  <span className="mt-2 block font-body-sm text-body-sm text-on-surface">
                    {s.value}
                  </span>
                ) : null}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
